import React, { useState, useCallback, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import HeaderNew from "../../../../../common/components/custom/header-new/HeaderNew";
import HubHeader from "../../../../../common/components/custom/hub-header/HubHeader";
import AssignmentMainContent, {
  AssignmentItem
} from "../../../../../common/components/custom/assignment-clause-card/AssignmentMainContent";
import ConfirmationModal from "../../../../../common/components/custom/confirmation-modal/ConfirmationModal";
import { ToastMessageUtil } from "../../../../../common/components/base/toast-message/ToastMessage.util";
import { ToastVariant } from "../../../../../common/enums/controls/toast.enum";
import { MainRoutes } from "../../../../../common/config";
import "./TagLibraryConfigList.scss";
import { Loading } from "modules/_shared/pages";
import { useAppSelector } from "../../../../../common/redux/core";
import {
  useGetAllTagLibraryConfigurationsQuery,
  useDeleteTagLibraryConfigurationMutation
} from "../../../data/api/queries/tagLibrary";
import {
  TagLibraryConfig,
  TagLibraryApiResponse
} from "../../../data/models/configuration-hub.model";

const TagLibraryConfigList: React.FC = () => {
  const navigate = useNavigate();

  // Use global Redux state for document type
  const { selectedDocumentType } = useAppSelector(
    (state) => state.documentType
  );

  // RTK Query hooks
  const {
    data: tagLibraryApiData,
    error: tagLibraryApiError,
    isLoading: isLoadingTagLibrary,
    refetch: refetchTagLibrary
  } = useGetAllTagLibraryConfigurationsQuery();

  const [deleteTagLibraryConfig, { isLoading: isDeletingFromApi }] =
    useDeleteTagLibraryConfigurationMutation();

  const [tagLibraryConfigs, setTagLibraryConfigs] = useState<AssignmentItem[]>(
    []
  );
  const [configError, setConfigError] = useState<string | null>(null);
  const [configurationList, setConfigurationList] = useState<AssignmentItem[]>(
    []
  );

  // Delete confirmation modal state
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [configToDelete, setConfigToDelete] = useState<{
    id: number;
    name: string;
    guidCode: string;
  } | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Transform API data to AssignmentItem format
  const transformTagLibraryData = useCallback(
    (apiResponse: TagLibraryApiResponse): AssignmentItem[] => {
      if (
        !apiResponse ||
        !apiResponse.success ||
        !Array.isArray(apiResponse.tagLibrary)
      ) {
        console.warn("Invalid API response structure:", apiResponse);
        return [];
      }

      // Helper function to format date to mm/dd/yyyy
      const formatDate = (dateString: string): string => {
        try {
          const date = new Date(dateString);
          return date.toLocaleDateString("en-US", {
            month: "2-digit",
            day: "2-digit",
            year: "numeric"
          });
        } catch (error) {
          console.error("Error formatting date:", dateString, error);
          return dateString; // Return original if formatting fails
        }
      };

      const transformedData = apiResponse.tagLibrary.map((config) => ({
        id: config.id,
        title: config.name,
        status: config.isActive ? "Active" : "Inactive",
        company: "admin",
        department: config.isCFIHOS ? "CFIHOS" : "Custom",
        createdBy: config.createdByName || "admin",
        createdOn: formatDate(config.createdDate),
        documentType: selectedDocumentType,
        guidCode: config.guidCode,
        description: config.description,
        cfiHOSId: config.cfiHOSId,
        isCFIHOS: config.isCFIHOS,
        order: config.order,
        configurationName: config.configurationName || undefined
      }));

      return transformedData;
    },
    [selectedDocumentType]
  );

  // Handle API data changes
  useEffect(() => {
    if (tagLibraryApiData) {
      try {
        const transformedConfigs = transformTagLibraryData(tagLibraryApiData);
        setTagLibraryConfigs(transformedConfigs);
        setConfigError(null);
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Data transformation error";
        setConfigError(errorMessage);
        console.error("Error transforming tag library data:", error);
      }
    }
  }, [tagLibraryApiData, transformTagLibraryData]);

  // Handle API errors
  useEffect(() => {
    if (tagLibraryApiError) {
      const errorMessage =
        "data" in tagLibraryApiError
          ? `API Error: ${tagLibraryApiError.data}`
          : "status" in tagLibraryApiError
          ? `HTTP ${tagLibraryApiError.status}: Failed to fetch tag library configurations`
          : "Failed to load tag library configurations";

      setConfigError(errorMessage);
      console.error("Tag Library API Error:", tagLibraryApiError);
    }
  }, [tagLibraryApiError]);

  // Update configuration list when configs change
  useEffect(() => {
    setConfigurationList(tagLibraryConfigs);
  }, [tagLibraryConfigs]);

  const handleEdit = useCallback(
    async (id: number) => {
      try {
        // Find the configuration to get its guidCode
        const config = tagLibraryConfigs.find((c) => c.id === id);
        if (!config || !config.guidCode) {
          ToastMessageUtil.show(
            "Failed to load configuration for editing - missing configuration ID",
            ToastVariant.ERROR
          );
          return;
        }

        // Navigate directly to create route with existing data from the list
        // The PUT API is for updating, not retrieving data, so we use what we have
        navigate(MainRoutes.CREATE_TAG_LIBRARY_CONFIG, {
          state: {
            editMode: true,
            configName: config.configurationName,
            categoryName: config.title,
            categoryDescription: config.description,
            documentType: selectedDocumentType,
            guidCode: config.guidCode,
            configurationId: id,
            configData: {
              id: config.id,
              name: config.title,
              description: config.description,
              cfiHOSId: config.cfiHOSId,
              isCFIHOS: config.isCFIHOS,
              order: config.order,
              guidCode: config.guidCode
            }
          }
        });
      } catch (error: any) {
        console.error("Error navigating to edit page:", error);
        const errorMessage =
          error?.message || "Error opening configuration for editing";
        ToastMessageUtil.show(errorMessage, ToastVariant.ERROR);
      }
    },
    [tagLibraryConfigs, selectedDocumentType, navigate]
  );

  const handleDelete = useCallback(
    async (id: number) => {
      // Find the configuration to get its name and guidCode
      const foundConfig = tagLibraryConfigs.find((config) => config.id === id);
      const configName = foundConfig?.title || `Configuration ${id}`;
      const guidCode = foundConfig?.guidCode;

      if (!guidCode) {
        ToastMessageUtil.show(
          "Unable to delete: Configuration ID not found",
          ToastVariant.ERROR
        );
        return;
      }

      // Set the configuration to delete and show modal
      setConfigToDelete({ id, name: configName, guidCode });
      setDeleteModalOpen(true);
    },
    [tagLibraryConfigs]
  );

  const handleDeleteConfirm = useCallback(async () => {
    if (!configToDelete) return;

    setIsDeleting(true);
    try {
      // Use the actual DELETE API with guidCode
      await deleteTagLibraryConfig(configToDelete.guidCode).unwrap();

      // Remove from local state
      setTagLibraryConfigs((prevConfigs) =>
        prevConfigs.filter((config) => config.id !== configToDelete.id)
      );

      ToastMessageUtil.show(
        `Tag library configuration "${configToDelete.name}" deleted successfully!`,
        ToastVariant.SUCCESS
      );
      setDeleteModalOpen(false);
      setConfigToDelete(null);
    } catch (error: any) {
      console.error("Error deleting tag library configuration:", error);
      const errorMessage =
        error?.data?.message ||
        error?.message ||
        "Failed to delete tag library configuration";
      ToastMessageUtil.show(
        `Error deleting configuration: ${errorMessage}`,
        ToastVariant.ERROR
      );
    } finally {
      setIsDeleting(false);
    }
  }, [configToDelete, deleteTagLibraryConfig]);

  const handleDeleteCancel = useCallback(() => {
    setDeleteModalOpen(false);
    setConfigToDelete(null);
  }, []);

  const handleView = useCallback(
    (id: number) => {
      // Find the complete configuration data for this ID
      const selectedConfig = tagLibraryConfigs.find(
        (config) => config.id === id
      );

      if (!selectedConfig) {
        console.error(
          `Configuration with ID ${id} not found in tagLibraryConfigs`
        );
        ToastMessageUtil.show(
          `Configuration with ID ${id} not found`,
          ToastVariant.ERROR
        );
        return;
      }

      navigate("/tag-library-config/enrich", {
        state: {
          // Required by TagEnrichmentPage
          fromSetup: true, // This tells the enrich page to use the passed masterObjectId
          masterObjectId: selectedConfig.id, // This is what the enrich page expects

          // Optional display data for the enrich page
          configName: selectedConfig.title,
          categoryName: selectedConfig.department, // CFIHOS or Custom
          categoryDescription: selectedConfig.description || "",

          // Additional data for backwards compatibility
          configurationId: selectedConfig.id,
          documentType: selectedDocumentType,
          configData: selectedConfig,
          guidCode: selectedConfig.guidCode
        }
      });
    },
    [navigate, selectedDocumentType, tagLibraryConfigs]
  );

  const handleNewConfiguration = useCallback(async () => {
    try {
      // Navigate to tag library creation page
      navigate(MainRoutes.CREATE_TAG_LIBRARY_CONFIG, {
        state: { documentType: selectedDocumentType }
      });
    } catch (errors) {
      console.error("Error navigating to tag library config creation:", errors);
    }
  }, [selectedDocumentType, navigate]);

  // Conditional action visibility functions
  const canEditItem = useCallback((item: AssignmentItem) => {
    // Hide edit action for system-created items
    return item.createdBy !== "System";
  }, []);

  const canDeleteItem = useCallback((item: AssignmentItem) => {
    // Hide delete action for system-created items
    return item.createdBy !== "System";
  }, []);

  // Show loading when configurations are being loaded
  if (isLoadingTagLibrary) {
    return (
      <>
        <HeaderNew showSearch={false} />
        <div className="assignment-container">
          <HubHeader
            className="header"
            title="Tag Library Configurations"
            description="Manage tag libraries with AI-powered enrichment and embeddings"
            showBackButton={true}
            backRoute="/configure"
          />
          <div className="assignment-content">
            <div className="loading-container">
              <Loading />
              <p style={{ textAlign: "center", marginTop: "1rem" }}>
                Loading tag library configurations...
              </p>
            </div>
          </div>
        </div>
      </>
    );
  }

  if (configError) {
    console.error("Error loading tag library configurations:", configError);
  }

  return (
    <>
      <HeaderNew showSearch={false} />
      <div className="assignment-container">
        <HubHeader
          className="header"
          title="Tag Library Configurations"
          description="Manage tag libraries with AI-powered enrichment and embeddings"
          showBackButton={true}
          backRoute="/configure"
        />

        {/* Error Display */}
        {configError && (
          <div className="error-message">
            <strong>Error:</strong> {configError}
          </div>
        )}

        {/* Main Content */}
        <div className="assignment-content">
          <AssignmentMainContent
            assignments={tagLibraryConfigs}
            configurationList={configurationList}
            setConfigurationList={setConfigurationList}
            showDropdown={false}
            showNewConfigButton={true}
            newConfigButtonText="New Tag Library"
            onNewConfigClick={handleNewConfiguration}
            cardsClickable={false}
            showActions={true}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onView={handleView}
            showDocumentTypeFilter={false}
            headerTitle="Available Tag Library Configurations"
            className="assignment-main-content"
            isLoading={false} // We handle loading at the component level
            // canEdit={canEditItem}
            // canDelete={canDeleteItem}
          />
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={deleteModalOpen}
        title="Delete Tag Library Configuration"
        message={`Are you sure you want to delete "${configToDelete?.name}"? This action cannot be undone.`}
        variant="danger"
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        isLoading={isDeleting || isDeletingFromApi}
      />
    </>
  );
};

export default TagLibraryConfigList;
